import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { Badge, Button, Callout, Code, Flex, IconButton, Text, TextField } from '@radix-ui/themes'
import { Form } from 'radix-ui'
import { Cross2Icon, EyeClosedIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import { Modal } from '@Components/common/modal'
import { SearchServiceAPI } from '@API/data-services/search-service'
import { RootState } from '@AppState/redux-store'
import {
  CreateSearchConfigRequest,
  CreateSearchConfigResponse,
  ResponseError,
} from '@Generated/back-office-client/src'
import { SearchCreateFormPhase } from './types'

export type CreateSearchConfigProps = {
  onClose: () => void
  onCreated: (hostname: string) => void
}

export const CreateSearchConfig = ({ onClose, onCreated }: CreateSearchConfigProps) => {
  const [name, setName] = useState('')
  const [hostname, setHostname] = useState('')
  const [loadBalancedEndpoint, setLoadBalancedEndpoint] = useState('')
  const [hosts, setHosts] = useState<string[]>([])
  const [newHost, setNewHost] = useState('')
  const [noResultsMessages, setNoResultsMessages] = useState<string[]>([])
  const [newNoResultMessage, setNewNoResultMessage] = useState('')
  const [readKey, setReadKey] = useState('')
  const [adminKey, setAdminKey] = useState('')
  const [showReadKey, setShowReadKey] = useState(false)
  const [showAdminKey, setShowAdminKey] = useState(false)
  const [genericError, setGenericError] = useState<string | null>(null)
  const [hostnameError, setHostnameError] = useState<string | null>(null)
  const [phase, setPhase] = useState<SearchCreateFormPhase>(SearchCreateFormPhase.FORM)
  const [revealedKey, setRevealedKey] = useState<string | null>(null)
  const [createdHostname, setCreatedHostname] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)

  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()

  const { mutate: createConfig, isPending } = useMutation({
    mutationFn: (request: CreateSearchConfigRequest) =>
      SearchServiceAPI.createSearchConfig(request),
    onSuccess: (response: CreateSearchConfigResponse) => {
      setRevealedKey(response.encodedAnalyticsKey)
      setCreatedHostname(response.hostname)
      setPhase(SearchCreateFormPhase.REVEAL)
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-search-collections`] })
    },
    onError: (err: unknown) => {
      setHostnameError(null)

      let status: number | undefined
      if (err instanceof ResponseError) {
        status = err.response.status
      }

      if (status === 409) {
        setHostnameError('A search config already exists for this hostname.')
        setGenericError(null)
        return
      }

      if (status === 422) {
        setGenericError('The request was rejected. Check your input and try again.')
        return
      }

      if (status === 401 || status === 403) {
        setGenericError("You don't have permission to create search configs.")
        return
      }

      setGenericError('Failed to create search config. Please try again.')
    },
  })

  const addHost = () => {
    const trimmed = newHost.trim()
    if (!trimmed || hosts.includes(trimmed)) {
      return
    }
    setHosts([...hosts, trimmed])
    setNewHost('')
    setGenericError(null)
  }

  const removeHost = (host: string) => {
    setHosts(hosts.filter((h) => h !== host))
  }

  const addNoResultMessage = () => {
    const trimmed = newNoResultMessage.trim()
    if (!trimmed || noResultsMessages.includes(trimmed)) {
      return
    }
    setNoResultsMessages([...noResultsMessages, trimmed])
    setNewNoResultMessage('')
  }

  const removeNoResultMessage = (message: string) => {
    setNoResultsMessages(noResultsMessages.filter((m) => m !== message))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (hosts.length === 0) {
      setGenericError('At least one host is required')
      return
    }

    setGenericError(null)
    setHostnameError(null)

    const request: CreateSearchConfigRequest = {
      name,
      hostname,
      loadBalancedEndpoint,
      hosts,
      readKey,
      adminKey,
      noResultsMessages,
    }

    createConfig(request)
  }

  return (
    <Modal
      onOpenChange={(open) => {
        if (open) {
          return
        }
        if (phase === SearchCreateFormPhase.REVEAL && createdHostname) {
          onCreated(createdHostname)
        }
        onClose()
      }}
      title={
        phase === SearchCreateFormPhase.FORM
          ? 'Create Search Configuration'
          : 'Search Config Created'
      }
      description={
        phase === SearchCreateFormPhase.FORM
          ? 'Create a new search configuration for this customer.'
          : 'Copy the analytics key now — it will not be shown again.'
      }
      hideCloseButton={phase === SearchCreateFormPhase.REVEAL}>
      {phase === SearchCreateFormPhase.FORM ? (
        <Form.Root onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <Text size="4" weight="bold">
              Create Search Configuration
            </Text>

            <Form.Field name="name">
              <Flex justify="between">
                <Form.Label>Name</Form.Label>
                <Form.Message className="FormMessage" match="valueMissing">
                  Name is required
                </Form.Message>
              </Flex>
              <Form.Control asChild required>
                <TextField.Root
                  placeholder="main-collection"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    setGenericError(null)
                  }}
                />
              </Form.Control>
            </Form.Field>

            <Form.Field name="hostname">
              <Flex justify="between">
                <Form.Label>Hostname</Form.Label>
                <Form.Message className="FormMessage" match="valueMissing">
                  Hostname is required
                </Form.Message>
              </Flex>
              <Form.Control asChild required>
                <TextField.Root
                  placeholder="typesenseCollectionName"
                  value={hostname}
                  onChange={(e) => {
                    setHostname(e.target.value)
                    setGenericError(null)
                    setHostnameError(null)
                  }}
                />
              </Form.Control>
              {hostnameError ? (
                <Form.Message className="FormMessage" forceMatch>
                  {hostnameError}
                </Form.Message>
              ) : null}
            </Form.Field>

            <Form.Field name="loadBalancedEndpoint">
              <Flex justify="between">
                <Form.Label>Load-Balanced Endpoint</Form.Label>
                <Form.Message className="FormMessage" match="valueMissing">
                  Load-balanced endpoint is required
                </Form.Message>
              </Flex>
              <Form.Control asChild required>
                <TextField.Root
                  placeholder="https://typesense.lottery.example.com"
                  value={loadBalancedEndpoint}
                  onChange={(e) => {
                    setLoadBalancedEndpoint(e.target.value)
                    setGenericError(null)
                  }}
                />
              </Form.Control>
            </Form.Field>

            <Form.Field name="hosts">
              <Form.Label>Hosts</Form.Label>
              <TextField.Root
                placeholder="https://node-1.typesense.lottery.example.com"
                value={newHost}
                onChange={(e) => setNewHost(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addHost()
                  }
                }}>
                <TextField.Slot side="right">
                  <Button type="button" size="1" onClick={addHost}>
                    Add
                  </Button>
                </TextField.Slot>
              </TextField.Root>
              <Flex gap="2" wrap="wrap">
                {hosts.map((host) => (
                  <Badge color="gray" key={host}>
                    {host}
                    <IconButton
                      type="button"
                      variant="ghost"
                      size="1"
                      onClick={() => removeHost(host)}
                      aria-label={`Remove ${host}`}>
                      <Cross2Icon />
                    </IconButton>
                  </Badge>
                ))}
              </Flex>
            </Form.Field>

            <Form.Field name="noResultsMessages">
              <Form.Label>No Results Messages</Form.Label>
              <TextField.Root
                placeholder="No results found"
                value={newNoResultMessage}
                onChange={(e) => setNewNoResultMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addNoResultMessage()
                  }
                }}>
                <TextField.Slot side="right">
                  <Button type="button" size="1" onClick={addNoResultMessage}>
                    Add
                  </Button>
                </TextField.Slot>
              </TextField.Root>
              <Flex gap="2" wrap="wrap">
                {noResultsMessages.map((message) => (
                  <Badge color="gray" key={message}>
                    {message}
                    <IconButton
                      type="button"
                      variant="ghost"
                      size="1"
                      onClick={() => removeNoResultMessage(message)}
                      aria-label={`Remove ${message}`}>
                      <Cross2Icon />
                    </IconButton>
                  </Badge>
                ))}
              </Flex>
            </Form.Field>

            <Form.Field name="readKey">
              <Flex justify="between">
                <Form.Label>Typesense Read Key</Form.Label>
                <Form.Message className="FormMessage" match="valueMissing">
                  Typesense read key is required
                </Form.Message>
              </Flex>
              <Form.Control asChild required>
                <TextField.Root
                  type={showReadKey ? 'text' : 'password'}
                  value={readKey}
                  onChange={(e) => {
                    setReadKey(e.target.value)
                    setGenericError(null)
                  }}>
                  <TextField.Slot side="right">
                    <IconButton
                      type="button"
                      variant="ghost"
                      onClick={() => setShowReadKey((v) => !v)}
                      aria-label={showReadKey ? 'Hide read key' : 'Show read key'}>
                      {showReadKey ? <EyeClosedIcon /> : <EyeOpenIcon />}
                    </IconButton>
                  </TextField.Slot>
                </TextField.Root>
              </Form.Control>
            </Form.Field>

            <Form.Field name="adminKey">
              <Flex justify="between">
                <Form.Label>Typesense Admin Key</Form.Label>
                <Form.Message className="FormMessage" match="valueMissing">
                  Typesense admin key is required
                </Form.Message>
              </Flex>
              <Form.Control asChild required>
                <TextField.Root
                  type={showAdminKey ? 'text' : 'password'}
                  value={adminKey}
                  onChange={(e) => {
                    setAdminKey(e.target.value)
                    setGenericError(null)
                  }}>
                  <TextField.Slot side="right">
                    <IconButton
                      type="button"
                      variant="ghost"
                      onClick={() => setShowAdminKey((v) => !v)}
                      aria-label={showAdminKey ? 'Hide admin key' : 'Show admin key'}>
                      {showAdminKey ? <EyeClosedIcon /> : <EyeOpenIcon />}
                    </IconButton>
                  </TextField.Slot>
                </TextField.Root>
              </Form.Control>
            </Form.Field>

            {genericError ? (
              <Callout.Root color="red">
                <Callout.Text>{genericError}</Callout.Text>
              </Callout.Root>
            ) : null}

            <Flex gap="3" justify="end">
              <Button
                type="button"
                variant="soft"
                color="gray"
                style={{ cursor: 'pointer' }}
                onClick={onClose}>
                Cancel
              </Button>
              <Form.Submit asChild>
                <Button loading={isPending} style={{ cursor: 'pointer' }}>
                  Create Search Config
                </Button>
              </Form.Submit>
            </Flex>
          </Flex>
        </Form.Root>
      ) : (
        <Flex direction="column" gap="4">
          <Text size="4" weight="bold">
            Search Config Created
          </Text>
          <Text size="2" color="red">
            This analytics key will only be shown once. Copy it now.
          </Text>
          <Code
            size="2"
            style={{
              wordBreak: 'break-all',
              padding: '12px',
              display: 'block',
            }}>
            {revealedKey}
          </Code>
          {copyError ? (
            <Callout.Root color="red">
              <Callout.Text>
                Couldn&apos;t access the clipboard. Select the key above and copy it manually.
              </Callout.Text>
            </Callout.Root>
          ) : null}
          <Flex gap="3" justify="end">
            <Button
              variant="soft"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (!revealedKey) {
                  return
                }
                navigator.clipboard
                  .writeText(revealedKey)
                  .then(() => {
                    setCopied(true)
                    setCopyError(false)
                  })
                  .catch(() => {
                    setCopyError(true)
                  })
              }}>
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </Button>
            <Button
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (createdHostname) {
                  onCreated(createdHostname)
                }
                onClose()
              }}>
              {"I've copied the key"}
            </Button>
          </Flex>
        </Flex>
      )}
    </Modal>
  )
}
